import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { FILM_TYPES, waLink } from "../lib/data";

export default function FilmTypes() {
  const [active, setActive] = useState(FILM_TYPES[0].id);
  const current = FILM_TYPES.find((f) => f.id === active);

  return (
    <section
      id="tipos"
      data-testid="film-types-section"
      className="relative bg-[#0A0B0E] py-24 lg:py-36 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-[0.3em]">
                Tipos de película
              </span>
            </div>
            <h2 className="font-display text-4xl lg:text-6xl leading-[1.05] tracking-tight text-white">
              La <span className="italic text-[#D4AF37]">película</span> ideal
              para tu proyecto.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-base lg:text-lg text-white/55 font-light leading-relaxed">
              Contamos con una amplia gama de películas para cada necesidad.
              Compártenos las medidas aproximadas por WhatsApp y te asesoramos
              sobre la opción correcta.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/5 pb-3">
          {FILM_TYPES.map((f) => (
            <button
              key={f.id}
              onClick={() => setActive(f.id)}
              data-testid={`film-tab-${f.id}`}
              className={`relative px-5 py-2.5 text-sm uppercase tracking-[0.15em] transition-colors ${
                active === f.id ? "text-[#D4AF37]" : "text-white/50 hover:text-white"
              }`}
            >
              {f.name}
              {active === f.id && (
                <motion.span
                  layoutId="film-underline"
                  className="absolute left-0 right-0 -bottom-3 h-px bg-[#D4AF37]"
                />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-12 gap-10 items-stretch"
          >
            <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[480px] rounded-2xl overflow-hidden border border-white/10">
              <img
                src={current.image}
                alt={current.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B0E] via-[#0A0B0E]/30 to-transparent" />
              <div className="absolute top-6 left-6 text-xs uppercase tracking-[0.25em] text-[#D4AF37] px-3 py-1.5 rounded-full border border-[#D4AF37]/30 bg-black/40 backdrop-blur-xl">
                {current.tagline}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col justify-between p-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                  Película
                </div>
                <h3 className="font-display text-4xl lg:text-5xl text-white tracking-tight mb-6 leading-tight">
                  {current.name}
                </h3>
                <p className="text-base text-white/65 font-light leading-relaxed mb-8">
                  {current.description}
                </p>
                <ul className="space-y-3 mb-10">
                  {current.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/75">
                      <Check size={14} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href={waLink(`Hola, quiero información sobre la película ${current.name}`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`film-cta-${current.id}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#E5C04A] transition-all hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] w-fit"
              >
                Cotizar {current.name} por WhatsApp →
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
